import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import {
  ReactFlow, Background, Controls, MiniMap, addEdge, useNodesState, useEdgesState,
  Handle, Position, ReactFlowProvider, useReactFlow, useUpdateNodeInternals,
  BaseEdge, EdgeLabelRenderer, getBezierPath,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Play, SpeakerHigh, Ear, WaveTriangle, GitBranch, PhoneCall, PhoneX, X,
  Plus, Trash, UploadSimple, DownloadSimple, WarningCircle, Hash, Flag, AddressBook, Robot,
  CaretUpDown, Check, MagicWand,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import api, { API, formatApiError, getStoredToken } from "@/lib/api";
import { RichHtmlField } from "@/components/RichHtmlEditorDialog";
import InstructionProfileEditor, {
  emptyInstructionProfile,
  instructionProfileToText,
  normalizeInstructionProfile,
} from "@/components/InstructionProfileEditor";
import AiAgentWizard from "@/components/AiAgentWizard";

export const KIND = {
  start: { label: "Start", color: "#22c55e", icon: Play },
  play_audio: { label: "Play Audio", color: "#6366f1", icon: SpeakerHigh },
  collect_info: { label: "Collect Customer Info", color: "#0d9488", icon: AddressBook },
  ai_llm: { label: "AI Agent", color: "#7c3aed", icon: Robot, dynamic: true },
  wait: { label: "Wait for Customer", color: "#f59e0b", icon: Ear,
    outputs: [{ id: "speech", label: "Speech" }, { id: "silence", label: "Silence" }] },
  intent: { label: "Customer Response", color: "#a855f7", icon: GitBranch, dynamic: true },
  dtmf: { label: "DTMF", color: "#0ea5e9", icon: Hash },
  silence: { label: "Silence", color: "#64748b", icon: WaveTriangle,
    outputs: [{ id: "retry", label: "Retry" }, { id: "max_reached", label: "Max reached" }] },
  transfer: { label: "Transfer", color: "#14b8a6", icon: PhoneCall,
    outputs: [{ id: "success", label: "Success" }, { id: "failure", label: "Failure" }] },
  hangup: { label: "Hang Up", color: "#ef4444", icon: PhoneX },
  end: { label: "End", color: "#334155", icon: Flag, terminal: true },
};

const PALETTE = ["play_audio", "collect_info", "ai_llm", "wait", "silence", "intent", "dtmf", "transfer", "hangup", "end"];

const LLM_PERSONALITIES = [
  { id: "professional", label: "Professional" },
  { id: "friendly", label: "Friendly" },
  { id: "consultative", label: "Consultative" },
  { id: "persuasive", label: "Persuasive" },
  { id: "empathetic", label: "Empathetic" },
  { id: "energetic", label: "Energetic" },
  { id: "calm", label: "Calm" },
  { id: "concise", label: "Concise" },
];
const LLM_ROLES = [
  { id: "sales", label: "Sales" },
  { id: "lead_qualification", label: "Lead Qualification" },
  { id: "appointment_booking", label: "Appointment Booking" },
  { id: "customer_support", label: "Customer Support" },
  { id: "survey_feedback", label: "Survey / Feedback" },
  { id: "information_faq", label: "Information / FAQ" },
  { id: "follow_up", label: "Follow-up" },
  { id: "collections_payment", label: "Collections / Payment" },
  { id: "receptionist", label: "Receptionist" },
  { id: "custom", label: "Custom" },
];
const LLM_ACTIONS = [
  { id: "continue", label: "Continue" },
  { id: "collect_info", label: "Collect Customer Information" },
  { id: "transfer", label: "Transfer" },
  { id: "hangup", label: "Hang Up" },
  { id: "book_appointment", label: "Book Appointment" },
];

/** Recommended languages for AI Agent (default: English). */
export const AI_AGENT_LANGUAGES = [
  { id: "en", flag: "🇺🇸", label: "English" },
  { id: "es", flag: "🇪🇸", label: "Spanish" },
  { id: "pt-BR", flag: "🇧🇷", label: "Portuguese (Brazil)" },
  { id: "es-419", flag: "🇲🇽", label: "Spanish (Latin America)" },
  { id: "fr", flag: "🇫🇷", label: "French" },
  { id: "de", flag: "🇩🇪", label: "German" },
  { id: "it", flag: "🇮🇹", label: "Italian" },
  { id: "tr", flag: "🇹🇷", label: "Turkish" },
  { id: "ar", flag: "🇸🇦", label: "Arabic" },
  { id: "hi", flag: "🇮🇳", label: "Hindi" },
  { id: "ur", flag: "🇵🇰", label: "Urdu" },
  { id: "bn", flag: "🇧🇩", label: "Bengali" },
  { id: "id", flag: "🇮🇩", label: "Indonesian" },
  { id: "ms", flag: "🇲🇾", label: "Malay" },
  { id: "fil", flag: "🇵🇭", label: "Filipino" },
  { id: "vi", flag: "🇻🇳", label: "Vietnamese" },
  { id: "th", flag: "🇹🇭", label: "Thai" },
  { id: "zh", flag: "🇨🇳", label: "Chinese (Mandarin)" },
  { id: "ja", flag: "🇯🇵", label: "Japanese" },
  { id: "ko", flag: "🇰🇷", label: "Korean" },
  { id: "ru", flag: "🇷🇺", label: "Russian" },
  { id: "nl", flag: "🇳🇱", label: "Dutch" },
  { id: "pl", flag: "🇵🇱", label: "Polish" },
  { id: "uk", flag: "🇺🇦", label: "Ukrainian" },
  { id: "el", flag: "🇬🇷", label: "Greek" },
  { id: "he", flag: "🇮🇱", label: "Hebrew" },
  { id: "ro", flag: "🇷🇴", label: "Romanian" },
  { id: "cs", flag: "🇨🇿", label: "Czech" },
  { id: "hu", flag: "🇭🇺", label: "Hungarian" },
  { id: "sv", flag: "🇸🇪", label: "Swedish" },
];

const TIMEOUT_ACTIONS = [
  { id: "transfer", label: "Transfer", hint: "Leave via the Max duration / Inactivity handle (wire to a Transfer node)." },
  { id: "hangup", label: "Hang Up", hint: "End the call immediately when the limit is hit." },
  { id: "goto", label: "Go to Node", hint: "Leave via the handle — connect it to any next node." },
];

const COLLECT_FIELDS = [
  { key: "name", label: "Name", type: "Name", question: "May I have your name please?" },
  { key: "email", label: "Email", type: "Email", question: "Could you please provide your email address?" },
  { key: "mobile", label: "Mobile", type: "Phone", question: "Could you please confirm your mobile number?" },
  { key: "company", label: "Company", type: "Company", question: "May I have your company name?" },
  { key: "address", label: "Address", type: "Address", question: "May I have your address?" },
  { key: "date_of_birth", label: "Date of Birth", type: "Date of Birth", question: "May I have your date of birth?" },
  { key: "custom", label: "Custom Field", type: "Custom", question: "" },
];
const DTMF_DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "*", "#"];

function defaultConfig(kind) {
  switch (kind) {
    case "play_audio": return { source: "upload", prompt_text: "", language: "en-US", voice: "female" };
    case "collect_info": return {
      language: "en",
      timeout_seconds: 8,
      on_timeout: "goto",
      confirm_answers: true,
      live_audio: true,
      intro_text: "",
      fields: COLLECT_FIELDS.map((f) => ({
        ...f,
        enabled: ["name", "email", "mobile"].includes(f.key),
        custom_label: "",
      })),
    };
    case "ai_llm": return {
      ai_bot_id: "",
      personality: "professional",
      role: "sales",
      objective: "",
      call_script: "",
      instructions: "",
      instruction_profile: {
        ...emptyInstructionProfile(),
        global_rules: "Ask one question at a time. Keep responses short and natural. Do not invent facts.",
      },
      knowledge_base_id: "",
      allowed_actions: ["continue", "collect_info", "transfer", "hangup"],
      max_turns: 8,
      listen_timeout_seconds: 8,
      voice: "female",
      language: "en",
      max_duration_minutes: 5,
      on_max_duration: "goto",
      inactivity_timeout_seconds: 15,
      on_inactivity: "goto",
      pause_when_customer_talks: true,
    };
    case "wait": return { timeout_seconds: 5, dtmf_enabled: false };
    case "silence": return { max_attempts: 2 };
    case "dtmf": return { keys: [{ id: "k1", digit: "1" }] };
    case "end": return {};
    case "intent": return { responses: [
      { id: "interested", label: "Interested", phrases: ["yes", "interested"] },
      { id: "not_interested", label: "Not Interested", phrases: ["no", "not interested"] },
      { id: "callback", label: "Callback", phrases: ["call later"] },
      { id: "unknown", label: "Unknown", phrases: [] },
    ] };
    case "transfer": return { transfer_type: "sip",
      sip: { host: "", port: 5060, transport: "UDP", destination: "", extra: "", auth_username: "", auth_password: "" },
      did: { number: "" },
      inbound_group: { id: "", name: "" },
      inbound_group_id: "" };
    case "hangup": return { reason: "" };
    default: return {};
  }
}

function outputsOf(node) {
  const kind = node.data.kind;
  const cfg = KIND[kind];
  if (kind === "intent") return (node.data.config?.responses || []).map((r) => ({ id: r.id, label: r.label }));
  // Stable handle order for AI Agent: regular actions first, then timeout exits.
  // Keeps Transfer / Hang Up / Max duration visually consistent and clickable.
  if (kind === "ai_llm") {
    const selected = node.data.config?.allowed_actions || [];
    const outs = LLM_ACTIONS
      .filter((a) => selected.includes(a.id))
      .map((a) => ({ id: a.id, label: a.label }));
    // Max duration / Inactivity always leave via canvas handles (Go to Node).
    outs.push({ id: "max_duration", label: "Max duration" });
    outs.push({ id: "inactivity", label: "Inactivity" });
    return outs;
  }
  if (kind === "dtmf") return (node.data.config?.keys || []).map((k) => ({ id: k.id, label: k.digit }));
  if (kind === "wait") {
    const outs = [{ id: "speech", label: "Speech" }, { id: "silence", label: "Silence" }];
    if (node.data.config?.dtmf_enabled) outs.push({ id: "dtmf", label: "DTMF" });
    return outs;
  }
  if (kind === "collect_info") {
    // New nodes default to "goto"; legacy nodes without the field keep a single edge.
    const onTimeout = node.data.config?.on_timeout != null
      ? node.data.config.on_timeout
      : "hangup";
    if (onTimeout === "goto" || onTimeout === "transfer") {
      return [
        { id: "done", label: "Done" },
        { id: "timeout", label: "Timeout" },
      ];
    }
    return [{ id: null, label: null }];
  }
  if (cfg?.outputs) return cfg.outputs;
  if (cfg?.terminal) return [];
  return [{ id: null, label: null }]; // single unnamed output (start, play_audio, hangup)
}

function handleLabel(node, handleId) {
  if (!handleId) return undefined;
  return outputsOf(node).find((o) => o.id === handleId)?.label;
}

// ---------------------------------------------------------------------------
// Deletable edge — click the connection to reveal a delete (×) button that
// disconnects it. Used for both admin & customer designers (disabled in readOnly).
// ---------------------------------------------------------------------------
function DeletableEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, label, markerEnd, style, selected, data }) {
  const { setEdges } = useReactFlow();
  const readOnly = !!data?.readOnly;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });
  const del = (evt) => {
    evt.stopPropagation();
    setEdges((eds) => eds.filter((e) => e.id !== id));
    toast.success("Connection removed");
  };
  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd}
        style={{ ...style, strokeWidth: selected ? 2.5 : 1.5, stroke: selected ? "hsl(var(--primary))" : (style?.stroke || "#b1b1b7") }} />
      <EdgeLabelRenderer>
        <div className="nodrag nopan group" data-testid={`flow-edge-${id}`}
          style={{ position: "absolute", transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, pointerEvents: "all" }}>
          <div className="flex items-center gap-1">
            {label && <span className="rounded bg-background/90 border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground shadow-sm whitespace-nowrap">{label}</span>}
            {!readOnly && (
              <button onClick={del} title="Delete connection" aria-label="Delete connection" data-testid={`delete-edge-${id}`}
                className={`w-4 h-4 rounded-full grid place-items-center bg-background border shadow-sm transition-opacity ${selected ? "opacity-100 border-destructive text-destructive" : "opacity-0 group-hover:opacity-100 border-border text-muted-foreground hover:text-destructive hover:border-destructive"}`}>
                <X className="w-2.5 h-2.5" weight="bold" />
              </button>
            )}
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}


// ---------------------------------------------------------------------------
// Node renderer
// ---------------------------------------------------------------------------
function FlowNode({ id, data, selected }) {
  const cfg = KIND[data.kind] || KIND.play_audio;
  const Icon = cfg.icon;
  const { setNodes, setEdges } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const outs = outputsOf({ id, data });
  const handleKey = outs.map((o) => o.id ?? "_").join("|");

  // When allowed actions / max-duration / inactivity handles change, React Flow
  // must recompute handle bounds or new ports (e.g. Max duration) won't connect.
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, handleKey, updateNodeInternals]);

  const remove = () => {
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  };

  const summary = (() => {
    const c = data.config || {};
    if (data.kind === "play_audio") {
      if (c.source === "tts" && (c.tts_clip_label || c.audio_filename)) {
        return `TTS · ${c.tts_clip_label || c.audio_filename}`;
      }
      return c.audio_filename || (c.prompt_text ? `“${c.prompt_text.slice(0, 26)}”` : "No audio set");
    }
    if (data.kind === "collect_info") {
      const on = (c.fields || []).filter((f) => f.enabled);
      if (!on.length) return "No fields selected";
      const lang = AI_AGENT_LANGUAGES.find((l) => l.id === (c.language || "en"));
      const fields = on.map((f) => (String(f.key || "").startsWith("custom") && f.custom_label) ? f.custom_label : f.label).join(", ");
      const intro = (c.intro_text || "").trim() ? "Intro set" : null;
      const bits = [lang ? `${lang.flag} ${lang.label}` : null, `Timeout ${c.timeout_seconds ?? 8}s`, intro, fields];
      return bits.filter(Boolean).join(" · ");
    }
    if (data.kind === "ai_llm") {
      const role = LLM_ROLES.find((r) => r.id === c.role)?.label || c.role || "Role";
      const pers = LLM_PERSONALITIES.find((p) => p.id === c.personality)?.label || c.personality || "";
      const lang = AI_AGENT_LANGUAGES.find((l) => l.id === (c.language || "en"));
      const voice = c.voice ? `Voice ${c.voice}` : null;
      const langLbl = lang ? `${lang.flag} ${lang.label}` : null;
      const maxM = c.max_duration_minutes != null ? `Max ${c.max_duration_minutes}m` : null;
      const script = (c.call_script || "").trim() ? "Call script" : null;
      return [langLbl, voice, role, pers, maxM, script].filter(Boolean).join(" · ");
    }
    if (data.kind === "wait") return `Timeout ${c.timeout_seconds ?? 5}s`;
    if (data.kind === "silence") return `Max ${c.max_attempts ?? 2} attempts`;
    if (data.kind === "intent") return `${(c.responses || []).length} responses`;
    if (data.kind === "transfer") {
      if (c.transfer_type === "did") return `DID ${c.did?.number || "—"}`;
      if (c.transfer_type === "inbound_group") return `Inbound group ${c.inbound_group?.name || "—"}`;
      return `SIP ${c.sip?.destination || "—"}`;
    }
    if (data.kind === "hangup") return c.reason ? `Reason: ${c.reason}` : "Hang up";
    if (data.kind === "dtmf") return `${(c.keys || []).length} key${(c.keys || []).length === 1 ? "" : "s"}`;
    if (data.kind === "end") return "Flow complete";
    return null;
  })();

  return (
    <div className={`rounded-xl border-2 bg-card shadow-sm group transition-shadow relative ${selected ? "ring-2 ring-offset-1 ring-primary" : ""}`}
      style={{ borderColor: cfg.color, minWidth: outs.length > 1 ? Math.max(220, outs.length * 110) : 210 }} data-testid={`flow-node-${data.kind}`}>
      {data.kind !== "start" && (
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={!data.readOnly}
          style={{ background: cfg.color, width: 12, height: 12, zIndex: 11 }}
        />
      )}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className="w-7 h-7 rounded-lg grid place-items-center text-white shrink-0" style={{ background: cfg.color }}>
          <Icon className="w-4 h-4" weight="bold" />
        </div>
        <div className="leading-tight min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{cfg.label}</p>
          <p className="text-sm font-medium truncate">{data.label || cfg.label}</p>
        </div>
        {data.kind !== "start" && (
          <button onClick={remove} data-testid={`delete-node-${id}`}
            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 rounded grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10">
            <X className="w-3.5 h-3.5" weight="bold" />
          </button>
        )}
      </div>
      {summary && <p className="px-3 pb-2 -mt-1 text-[11px] text-muted-foreground truncate">{summary}</p>}

      {outs.length > 1 ? (
        <div className="relative px-2 pb-4 pt-0.5" style={{ minHeight: 36 }}>
          {/* Labels only — do NOT nest Handles in relative flex cells (breaks left %). */}
          <div className="flex justify-between gap-1 pointer-events-none">
            {outs.map((o) => (
              <div key={`lbl-${o.id}`} className="flex-1 text-center min-w-0">
                <span className="text-[9px] leading-tight text-muted-foreground block px-0.5 truncate" title={o.label}>
                  {o.label}
                </span>
              </div>
            ))}
          </div>
          {outs.map((o, i) => (
            <Handle
              key={o.id ?? `out-${i}`}
              type="source"
              id={o.id == null ? undefined : String(o.id)}
              position={Position.Bottom}
              isConnectable={!data.readOnly}
              title={o.label || "Connect"}
              style={{
                left: `${((i + 0.5) / outs.length) * 100}%`,
                transform: "translate(-50%, 50%)",
                background: cfg.color,
                width: 12,
                height: 12,
                border: "2px solid #fff",
                zIndex: 11,
              }}
            />
          ))}
        </div>
      ) : outs.length === 1 ? (
        <Handle
          type="source"
          id={outs[0].id == null ? undefined : String(outs[0].id)}
          position={Position.Bottom}
          isConnectable={!data.readOnly}
          style={{ background: cfg.color, width: 12, height: 12, zIndex: 11 }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audio player (auth via query param, since <audio> can't send headers)
// ---------------------------------------------------------------------------
function AudioPreview({ fileId }) {
  const token = getStoredToken();
  return <audio controls src={`${API}/audio/${fileId}?auth=${encodeURIComponent(token || "")}`} className="w-full mt-2 h-9" data-testid="audio-preview" />;
}

// ---------------------------------------------------------------------------
// Numeric field that allows free typing (clamp on blur). React Flow otherwise
// steals Backspace while a node is selected, so we stop key propagation.
// ---------------------------------------------------------------------------
function NumberField({
  value,
  onCommit,
  min = 1,
  max = 999,
  fallback,
  testid,
  className,
}) {
  const def = fallback != null ? fallback : min;
  const [draft, setDraft] = useState(value != null && value !== "" ? String(value) : String(def));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value != null && value !== "" ? String(value) : String(def));
  }, [value, def, focused]);

  const commit = (raw) => {
    const n = Number(String(raw).trim());
    const next = Number.isFinite(n) ? Math.max(min, Math.min(max, Math.round(n))) : def;
    setDraft(String(next));
    onCommit(next);
  };

  const stopKeys = (e) => e.stopPropagation();

  return (
    <Input
      data-testid={testid}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className={className}
      value={draft}
      onFocus={(e) => { setFocused(true); e.target.select(); }}
      onBlur={() => { setFocused(false); commit(draft); }}
      onChange={(e) => {
        const v = e.target.value.replace(/[^\d]/g, "");
        setDraft(v);
      }}
      onKeyDown={stopKeys}
      onKeyUp={stopKeys}
      onKeyPress={stopKeys}
    />
  );
}

// ---------------------------------------------------------------------------
// Language smart-search (AI Agent)
// ---------------------------------------------------------------------------
function LanguageCombobox({ value, onChange, testid = "ai-llm-language" }) {
  const [open, setOpen] = useState(false);
  const selected = AI_AGENT_LANGUAGES.find((l) => l.id === (value || "en")) || AI_AGENT_LANGUAGES[0];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3"
        >
          <span className="truncate">{selected.flag} {selected.label}</span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search language…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No language found.</CommandEmpty>
            <CommandGroup>
              {AI_AGENT_LANGUAGES.map((l) => (
                <CommandItem
                  key={l.id}
                  value={`${l.label} ${l.id}`}
                  data-testid={`${testid}-option-${l.id}`}
                  onSelect={() => { onChange(l.id); setOpen(false); }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${selected.id === l.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="mr-1.5">{l.flag}</span>{l.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Config panel
// ---------------------------------------------------------------------------
function inboundGroupId(cfg) {
  return String(cfg?.inbound_group?.id || cfg?.inbound_group_id || "").trim();
}

function ConfigPanel({ node, setNodes, setEdges, onClose, collectTts, aiBots, knowledgeBases, inboundGroups }) {
  const [uploading, setUploading] = useState(false);
  const [ttsClips, setTtsClips] = useState([]);
  const [ttsLoading, setTtsLoading] = useState(false);
  const [ttsSelecting, setTtsSelecting] = useState(false);
  const kind = node.data.kind;
  const cfg = node.data.config || {};

  const setData = (dataPatch) => setNodes((nds) => nds.map((n) => n.id === node.id ? { ...n, data: { ...n.data, ...dataPatch } } : n));
  const patch = (cfgPatch) => setNodes((nds) => nds.map((n) => n.id === node.id ? { ...n, data: { ...n.data, config: { ...n.data.config, ...cfgPatch } } } : n));

  // One-time migrate legacy free-text Instructions → structured profile.
  // Also force Max duration / Inactivity to Go-to-Node (canvas handles).
  useEffect(() => {
    if (kind !== "ai_llm") return;
    const fixes = {};
    if ((cfg.on_max_duration || "goto") !== "goto") fixes.on_max_duration = "goto";
    if ((cfg.on_inactivity || "goto") !== "goto") fixes.on_inactivity = "goto";
    const profile = cfg.instruction_profile;
    const hasProfile = profile
      && typeof profile === "object"
      && (
        (profile.about_you || "").trim()
        || (profile.global_rules || "").trim()
        || (profile.your_role || "").trim()
        || (Array.isArray(profile.rules) && profile.rules.length)
      );
    if (!hasProfile) {
      const legacy = (cfg.instructions || "").trim();
      if (legacy) {
        const migrated = normalizeInstructionProfile(null, legacy);
        fixes.instruction_profile = migrated;
        fixes.instructions = instructionProfileToText(migrated);
      }
    }
    if (Object.keys(fixes).length) patch(fixes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, kind]);

  const toggleAction = (actionId, on) => {
    const cur = cfg.allowed_actions || [];
    let next = on ? (cur.includes(actionId) ? cur : cur.concat(actionId)) : cur.filter((a) => a !== actionId);
    // Live Gemini needs "continue" so the model can listen/talk without exiting.
    // Exit-only sets (e.g. book_appointment+hangup) cause greeting→tool with no hear.
    if (next.length && !next.includes("continue")) {
      next = ["continue", ...next];
    }
    // Drop edges for removed action handles
    if (!on) setEdges((eds) => eds.filter((e) => !(e.source === node.id && e.sourceHandle === actionId)));
    patch({ allowed_actions: next.length ? next : ["continue"] });
  };

  const uploadAudio = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/audio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let duration = null;
      try {
        duration = await new Promise((res, rej) => {
          const a = document.createElement("audio");
          a.preload = "metadata";
          a.onloadedmetadata = () => res(Math.round(a.duration));
          a.onerror = rej;
          a.src = URL.createObjectURL(f);
        });
      } catch { /* duration optional */ }
      patch({
        source: "upload",
        audio_file_id: data.id,
        audio_filename: data.original_filename,
        duration,
        audio_warning: data.warning || null,
        telephony_compatible: data.telephony_compatible,
        tts_clip_id: null,
        tts_clip_label: null,
      });
      if (data.warning) toast.warning(data.warning);
      else toast.success("Audio uploaded (telephony-compatible)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  // Load Text-to-Speech clips when Play Audio source is TTS.
  useEffect(() => {
    if (kind !== "play_audio") return undefined;
    if ((cfg.source || "upload") !== "tts") return undefined;
    let cancelled = false;
    setTtsLoading(true);
    api.get("/speech/clips")
      .then(({ data }) => {
        if (!cancelled) setTtsClips(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setTtsClips([]);
          toast.error(formatApiError(err.response?.data?.detail) || "Could not load Text-to-Speech voices.");
        }
      })
      .finally(() => { if (!cancelled) setTtsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, cfg.source, node.id]);

  const selectTtsClip = async (clipId) => {
    if (!clipId) return;
    const clip = ttsClips.find((c) => c.id === clipId);
    setTtsSelecting(true);
    try {
      const { data } = await api.post(`/audio/from-tts-clip/${encodeURIComponent(clipId)}`);
      const label = clip
        ? `${clip.voice || "Voice"} · ${(clip.text || "").trim().slice(0, 40)}${(clip.text || "").length > 40 ? "…" : ""}`
        : (data.original_filename || "TTS voice");
      patch({
        source: "tts",
        tts_clip_id: clipId,
        tts_clip_label: label,
        audio_file_id: data.id,
        audio_filename: data.original_filename || label,
        duration: clip?.duration_seconds != null ? Math.round(Number(clip.duration_seconds)) : null,
        audio_warning: data.warning || null,
        telephony_compatible: data.telephony_compatible,
        // Keep spoken text for chat transcripts (never show the .wav filename).
        prompt_text: (clip?.text || "").trim(),
      });
      if (data.warning) toast.warning(data.warning);
      else toast.success("Text-to-Speech voice selected — staged for FreeSWITCH");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setTtsSelecting(false);
    }
  };

  const addResponse = () => {
    const id = `r_${Date.now().toString(36)}`;
    patch({ responses: [...(cfg.responses || []), { id, label: "New response", phrases: [] }] });
  };
  const updateResponse = (rid, key, val) =>
    patch({ responses: (cfg.responses || []).map((r) => r.id === rid ? { ...r, [key]: val } : r) });
  const deleteResponse = (rid) => {
    patch({ responses: (cfg.responses || []).filter((r) => r.id !== rid) });
    setEdges((eds) => eds.filter((e) => !(e.source === node.id && e.sourceHandle === rid)));
  };

  const addKey = () => patch({ keys: [...(cfg.keys || []), { id: `k_${Date.now().toString(36)}`, digit: "1" }] });
  const updateKey = (kid, digit) => patch({ keys: (cfg.keys || []).map((k) => k.id === kid ? { ...k, digit } : k) });
  const deleteKey = (kid) => {
    patch({ keys: (cfg.keys || []).filter((k) => k.id !== kid) });
    setEdges((eds) => eds.filter((e) => !(e.source === node.id && e.sourceHandle === kid)));
  };
  const toggleWaitDtmf = (enabled) => {
    patch({ dtmf_enabled: enabled });
    if (!enabled) setEdges((eds) => eds.filter((e) => !(e.source === node.id && e.sourceHandle === "dtmf")));
  };

  const isCustomField = (f) => String(f?.key || "").startsWith("custom");
  const patchField = (key, rowPatch) => patch({
    fields: (cfg.fields || []).map((row) => row.key === key ? { ...row, ...rowPatch } : row),
  });
  const addCustomQuestion = () => {
    const key = `custom_${Date.now().toString(36)}`;
    patch({
      fields: [...(cfg.fields || []), {
        key, label: "Custom Field", type: "Custom", enabled: true,
        custom_label: "", question: "",
      }],
    });
  };
  const removeCustomQuestion = (key) => {
    patch({ fields: (cfg.fields || []).filter((row) => row.key !== key) });
  };
  const setCollectTimeoutAction = (value) => {
    const prev = cfg.on_timeout || "goto";
    if ((prev === "transfer" || prev === "goto") && value === "hangup") {
      setEdges((eds) => eds.filter((e) => !(e.source === node.id && (
        e.sourceHandle === "timeout" || e.sourceHandle === "done"
      ))));
    }
    patch({ on_timeout: value });
  };

  return (
    <div
      className="w-80 shrink-0 border-l border-border pl-4 overflow-y-auto"
      data-testid="node-config-panel"
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold">{KIND[kind]?.label} settings</p>
        <button onClick={onClose} data-testid="close-config-panel" className="w-6 h-6 rounded grid place-items-center hover:bg-muted"><X className="w-4 h-4" /></button>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Node label</Label>
          <Input data-testid="config-label-input" value={node.data.label || ""} onChange={(e) => setData({ label: e.target.value })} />
        </div>

        {kind === "ai_llm" && (
          <label className="flex items-start gap-2.5 text-sm cursor-pointer select-none rounded-md border bg-muted/30 px-2.5 py-2" data-testid="ai-llm-pause-when-talk">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={cfg.pause_when_customer_talks !== false}
              onChange={(e) => patch({ pause_when_customer_talks: e.target.checked })}
            />
            <span>
              <span className="font-medium">Pause when customer talk</span>
              <span className="block text-[11px] text-muted-foreground mt-0.5">
                If the AI is speaking and the customer talks, stop and listen (realtime). Applies only to this AI Agent node.
              </span>
            </span>
          </label>
        )}

        {kind === "start" && <p className="text-xs text-muted-foreground">The entry point of the call. No configuration needed.</p>}

        {kind === "ai_llm" && (
          <div className="space-y-3" data-testid="ai-llm-config">
            {!(aiBots || []).length && (
              <p className="text-[11px] text-[hsl(var(--warning))]" data-testid="ai-llm-no-bot">
                No AI Bot is assigned to this account. Ask an admin to assign one under AI Bots.
              </p>
            )}
            {(collectTts?.engine === "piper" || collectTts?.paid) && collectTts?.voices?.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Voice</Label>
                <Select value={cfg.voice || undefined} onValueChange={(v) => patch({ voice: v })}>
                  <SelectTrigger data-testid="ai-llm-voice"><SelectValue placeholder="Select a voice" /></SelectTrigger>
                  <SelectContent>
                    {(collectTts.voices || []).map((v) => (
                      <SelectItem key={v.name} value={v.name}>{v.name} — {v.gender} · {v.accent}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  {collectTts.paid ? "This voice speaks the AI replies." : "AI engine voice that speaks the AI replies."}
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Language</Label>
              <LanguageCombobox
                value={cfg.language || "en"}
                onChange={(v) => patch({ language: v })}
              />
              <p className="text-[11px] text-muted-foreground">AI Agent will speak in this language for the whole node.</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Personality</Label>
              <Select value={cfg.personality || "professional"} onValueChange={(v) => patch({ personality: v })}>
                <SelectTrigger data-testid="ai-llm-personality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LLM_PERSONALITIES.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={cfg.role || "sales"} onValueChange={(v) => patch({ role: v })}>
                <SelectTrigger data-testid="ai-llm-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LLM_ROLES.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <RichHtmlField
              label="Objective"
              testid="ai-llm-objective"
              value={cfg.objective || ""}
              onChange={(v) => patch({ objective: v })}
              maxLength={4000}
              rowsHint="small"
              placeholder="What should the AI accomplish at this point in the call?"
            />
            <RichHtmlField
              label="Call Script"
              testid="ai-llm-call-script"
              value={cfg.call_script || ""}
              onChange={(v) => patch({ call_script: v })}
              maxLength={8000}
              rowsHint="large"
              placeholder="Write how the AI should open and run the call (IF reception / owner / other)…"
              hint="The AI Agent starts from this script. Use IF / ELSE style branches for reception vs owner. Do not put secrets here."
            />
            <InstructionProfileEditor
              value={cfg.instruction_profile}
              legacyInstructions={cfg.instructions || ""}
              allowedActions={cfg.allowed_actions || []}
              actionOptions={LLM_ACTIONS}
              onChange={(profile, text) => patch({
                instruction_profile: normalizeInstructionProfile(profile),
                instructions: text || instructionProfileToText(profile),
              })}
            />
            <div className="space-y-1.5">
              <Label className="text-xs">Knowledge Base</Label>
              <Select value={cfg.knowledge_base_id || "__none__"} onValueChange={(v) => patch({ knowledge_base_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="ai-llm-knowledge"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {(knowledgeBases || []).map((k) => (
                    <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Manage knowledge under Knowledge Bases.</p>
            </div>

            <div className="rounded-md border p-3 space-y-3" data-testid="ai-llm-max-duration">
              <div>
                <Label className="text-xs">Maximum Talk Time</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">Caps AI Agent usage on this node (metered resource).</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Max Duration (minutes)</Label>
                <NumberField
                  testid="ai-llm-max-duration-min"
                  value={cfg.max_duration_minutes ?? 5}
                  min={1}
                  max={60}
                  fallback={5}
                  onCommit={(n) => patch({ max_duration_minutes: n, on_max_duration: "goto" })}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                When time is up, leave via the Max duration handle — connect it to the next node.
              </p>
            </div>

            <div className="rounded-md border p-3 space-y-3" data-testid="ai-llm-inactivity">
              <div>
                <Label className="text-xs">Customer Inactivity Timeout</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">If the caller stops responding, leave via the Inactivity handle.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Timeout (seconds)</Label>
                <NumberField
                  testid="ai-llm-inactivity-sec"
                  value={cfg.inactivity_timeout_seconds ?? 15}
                  min={5}
                  max={120}
                  fallback={15}
                  onCommit={(n) => patch({ inactivity_timeout_seconds: n, on_inactivity: "goto" })}
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Connect the Inactivity handle on this node to the next step.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Allowed actions</Label>
              <div className="space-y-1.5">
                {LLM_ACTIONS.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox" data-testid={`ai-llm-action-${a.id}`}
                      checked={(cfg.allowed_actions || []).includes(a.id)}
                      onChange={(e) => toggleAction(a.id, e.target.checked)} />
                    {a.label}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">Connect each action handle to the next step. The LLM only returns the action; the flow engine runs it.</p>
            </div>
          </div>
        )}

        {kind === "collect_info" && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Works like AI Agent: live duplex conversation that extracts clean values,
              re-asks invalid email/phone, and can branch on silence timeout.
            </p>

            <div className="space-y-1.5">
              <Label className="text-xs">Intro text (optional)</Label>
              <Textarea
                rows={3}
                data-testid="collect-intro-text"
                value={cfg.intro_text || ""}
                onChange={(e) => patch({ intro_text: e.target.value })}
                placeholder="Played once when this node starts — e.g. Thanks, I just need a few details."
              />
              <p className="text-[11px] text-muted-foreground">
                Spoken once when the call reaches this node, before the first question.
                Leave empty to skip any greeting and ask the first field question immediately.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Language</Label>
              <LanguageCombobox value={cfg.language || "en"} onChange={(v) => patch({ language: v })} testid="collect-language" />
              <p className="text-[11px] text-muted-foreground">Questions and confirmations are spoken in this language.</p>
            </div>

            {(collectTts?.engine === "piper" || collectTts?.paid) && collectTts?.voices?.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Voice</Label>
                <Select value={cfg.voice || undefined} onValueChange={(v) => patch({ voice: v })}>
                  <SelectTrigger data-testid="collect-voice"><SelectValue placeholder="Select a voice" /></SelectTrigger>
                  <SelectContent>
                    {(collectTts.voices || []).map((v) => (
                      <SelectItem key={v.name} value={v.name}>{v.name} — {v.gender} · {v.accent}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs">No-reply timeout (sec)</Label>
                <NumberField
                  testid="collect-timeout"
                  min={3} max={120} fallback={8}
                  value={cfg.timeout_seconds ?? 8}
                  onCommit={(n) => patch({ timeout_seconds: n })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">On timeout</Label>
                <Select
                  value={cfg.on_timeout != null ? cfg.on_timeout : "hangup"}
                  onValueChange={setCollectTimeoutAction}
                >
                  <SelectTrigger data-testid="collect-on-timeout"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEOUT_ACTIONS.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              After the bot asks a question, if the customer stays silent this many seconds,
              take the On timeout action
              {(cfg.on_timeout === "goto" || cfg.on_timeout === "transfer")
                ? " (connect the Timeout handle)."
                : " (hang up)."}
            </p>

            <div className="flex items-center justify-between">
              <Label className="text-xs">Fields to collect</Label>
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs"
                data-testid="collect-add-custom" onClick={addCustomQuestion}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Custom question
              </Button>
            </div>
            {(cfg.fields || []).map((f) => (
              <div key={f.key} className="rounded-lg border border-border p-2.5 space-y-2" data-testid={`collect-field-${f.key}`}>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none flex-1 min-w-0">
                    <input type="checkbox" checked={!!f.enabled} data-testid={`collect-toggle-${f.key}`}
                      onChange={(e) => patchField(f.key, { enabled: e.target.checked })} />
                    <span className="truncate">{isCustomField(f) ? (f.custom_label || "Custom Field") : f.label}</span>
                  </label>
                  {isCustomField(f) && (
                    <button type="button" data-testid={`collect-remove-${f.key}`}
                      className="w-6 h-6 rounded grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      onClick={() => removeCustomQuestion(f.key)}>
                      <Trash className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {f.enabled && (
                  <div className="space-y-2 pl-6">
                    <p className="text-[11px] text-muted-foreground">Type: {f.type}</p>
                    {isCustomField(f) && (
                      <div className="space-y-1">
                        <Label className="text-xs">Field name</Label>
                        <Input className="h-8" data-testid={`collect-custom-label-${f.key}`} value={f.custom_label || ""}
                          onChange={(e) => patchField(f.key, { custom_label: e.target.value })}
                          placeholder="Account number" />
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-xs">Question</Label>
                      <Textarea rows={2} data-testid={`collect-question-${f.key}`} value={f.question || ""}
                        onChange={(e) => patchField(f.key, { question: e.target.value })}
                        placeholder="May I have your name please?" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {kind === "play_audio" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Audio source</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="play-audio-source">
                <button
                  type="button"
                  data-testid="play-audio-source-upload"
                  disabled={uploading || ttsSelecting}
                  onClick={() => patch({
                    source: "upload",
                    tts_clip_id: null,
                    tts_clip_label: null,
                    audio_file_id: cfg.source === "tts" ? null : cfg.audio_file_id,
                    audio_filename: cfg.source === "tts" ? null : cfg.audio_filename,
                    audio_warning: cfg.source === "tts" ? null : cfg.audio_warning,
                    telephony_compatible: cfg.source === "tts" ? null : cfg.telephony_compatible,
                    duration: cfg.source === "tts" ? null : cfg.duration,
                  })}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                    (cfg.source || "upload") === "upload"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted/50"
                  }`}
                >
                  Upload
                </button>
                <button
                  type="button"
                  data-testid="play-audio-source-tts"
                  disabled={uploading || ttsSelecting}
                  onClick={() => patch({
                    source: "tts",
                    audio_file_id: cfg.source === "tts" ? cfg.audio_file_id : null,
                    audio_filename: cfg.source === "tts" ? cfg.audio_filename : null,
                    prompt_text: "",
                  })}
                  className={`rounded-lg border px-3 py-2 text-xs font-semibold transition-colors ${
                    cfg.source === "tts"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:bg-muted/50"
                  }`}
                >
                  Text to Speech
                </button>
              </div>
            </div>

            {(cfg.source || "upload") === "upload" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Prerecorded audio (WAV, 8 kHz mono PCM/G.711 preferred)</Label>
                <label className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-3 text-sm cursor-pointer hover:bg-muted/50" data-testid="audio-upload-label">
                  <UploadSimple className="w-4 h-4" weight="bold" />
                  {uploading ? "Uploading…" : (cfg.audio_filename || "Choose audio file")}
                  <input type="file" accept="audio/*" className="hidden" onChange={uploadAudio} data-testid="audio-upload-input" disabled={uploading} />
                </label>
              </div>
            )}

            {cfg.source === "tts" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Select from Text to Speech</Label>
                <Select
                  value={cfg.tts_clip_id || undefined}
                  onValueChange={selectTtsClip}
                  disabled={ttsLoading || ttsSelecting}
                >
                  <SelectTrigger data-testid="play-audio-tts-select" className="h-9">
                    <SelectValue placeholder={ttsLoading ? "Loading voices…" : ttsSelecting ? "Staging for FreeSWITCH…" : "Choose a saved voice…"} />
                  </SelectTrigger>
                  <SelectContent>
                    {ttsClips.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {(c.voice || "Voice")}
                        {c.engine === "free" ? " · free" : " · paid"}
                        {" — "}
                        {(c.text || "").trim().slice(0, 48) || "(no text)"}
                        {(c.text || "").length > 48 ? "…" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!ttsLoading && ttsClips.length === 0 && (
                  <p className="text-[11px] text-muted-foreground">
                    No saved voices yet. Create one under <span className="font-medium">Text to Speech</span>, then come back.
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Selecting a voice converts it to 8 kHz mono and stages it to FreeSWITCH with your other campaign prompts.
                </p>
              </div>
            )}

            {cfg.audio_file_id && <AudioPreview fileId={cfg.audio_file_id} />}
            {cfg.duration != null && <p className="text-[11px] text-muted-foreground">Duration ~{cfg.duration}s</p>}
            {cfg.audio_warning && (
              <p className="text-[11px] text-[hsl(var(--warning))] flex items-start gap-1" data-testid="audio-warning">
                <WarningCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" weight="fill" />{cfg.audio_warning}
              </p>
            )}
            {cfg.audio_file_id && cfg.telephony_compatible && (
              <p className="text-[11px] text-[hsl(var(--success))]">✓ Telephony-compatible (8 kHz mono) — ready for FreeSWITCH.</p>
            )}
          </>
        )}

        {kind === "wait" && (
          <div className="space-y-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Maximum wait time (seconds)</Label>
              <Input data-testid="wait-timeout" type="number" min={1} value={cfg.timeout_seconds ?? 5} onChange={(e) => patch({ timeout_seconds: Number(e.target.value) })} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" data-testid="wait-dtmf-toggle" checked={!!cfg.dtmf_enabled} onChange={(e) => toggleWaitDtmf(e.target.checked)} />
              Accept DTMF key press (adds a DTMF branch)
            </label>
            <p className="text-[11px] text-muted-foreground">Branches: <b>Speech</b>, <b>Silence</b> (timeout){cfg.dtmf_enabled ? ", " : ""}{cfg.dtmf_enabled && <b>DTMF</b>}.</p>
          </div>
        )}

        {kind === "silence" && (
          <div className="space-y-1.5">
            <Label className="text-xs">Max re-prompt attempts</Label>
            <Input data-testid="silence-attempts" type="number" min={1} value={cfg.max_attempts ?? 2} onChange={(e) => patch({ max_attempts: Number(e.target.value) })} />
            <p className="text-[11px] text-muted-foreground mt-1"><b>Retry</b> re-prompts the customer; <b>Max reached</b> fires after the limit.</p>
          </div>
        )}

        {kind === "intent" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Response options (branches)</Label>
              <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={addResponse} data-testid="intent-add-response"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            </div>
            {(cfg.responses || []).map((r) => (
              <div key={r.id} className="rounded-lg border border-border p-2.5 space-y-2" data-testid={`intent-response-${r.id}`}>
                <div className="flex gap-1.5">
                  <Input className="h-8" data-testid={`intent-response-label-${r.id}`} value={r.label} onChange={(e) => updateResponse(r.id, "label", e.target.value)} placeholder="Interested" />
                  <button onClick={() => deleteResponse(r.id)} data-testid={`intent-response-delete-${r.id}`} className="w-8 h-8 rounded grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash className="w-4 h-4" /></button>
                </div>
                <PhrasesInput
                  testid={`intent-response-phrases-${r.id}`}
                  value={r.phrases}
                  onCommit={(arr) => updateResponse(r.id, "phrases", arr)} />
              </div>
            ))}
            {(cfg.responses || []).length === 0 && <p className="text-[11px] text-destructive">Add at least one response option.</p>}
          </div>
        )}

        {kind === "dtmf" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Expected keys (each is a branch)</Label>
              <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={addKey} data-testid="dtmf-add-key"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            </div>
            {(cfg.keys || []).map((k) => (
              <div key={k.id} className="flex gap-1.5 items-center" data-testid={`dtmf-key-${k.id}`}>
                <Select value={k.digit} onValueChange={(v) => updateKey(k.id, v)}>
                  <SelectTrigger data-testid={`dtmf-key-select-${k.id}`} className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{DTMF_DIGITS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground flex-1">Press “{k.digit}”</span>
                <button onClick={() => deleteKey(k.id)} data-testid={`dtmf-key-delete-${k.id}`} className="w-8 h-8 rounded grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash className="w-4 h-4" /></button>
              </div>
            ))}
            {(cfg.keys || []).length === 0 && <p className="text-[11px] text-destructive">Add at least one key (0–9, * or #).</p>}
            <p className="text-[11px] text-muted-foreground">Each configured key gets its own outgoing branch (executed by FreeSWITCH later).</p>
          </div>
        )}

        {kind === "transfer" && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Transfer type</Label>
              <Select value={cfg.transfer_type || "sip"} onValueChange={(v) => patch({ transfer_type: v })}>
                <SelectTrigger data-testid="transfer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sip">External SIP Server</SelectItem>
                  <SelectItem value="inbound_group">Inbound group</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(cfg.transfer_type || "sip") === "sip" ? (
              <div className="space-y-2">
                <div className="space-y-1.5"><Label className="text-xs">SIP server IP / domain</Label>
                  <Input data-testid="transfer-sip-host" value={cfg.sip?.host || ""} onChange={(e) => patch({ sip: { ...cfg.sip, host: e.target.value } })} placeholder="sip.example.com" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label className="text-xs">Port</Label>
                    <Input data-testid="transfer-sip-port" type="number" value={cfg.sip?.port ?? 5060} onChange={(e) => patch({ sip: { ...cfg.sip, port: Number(e.target.value) } })} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Transport</Label>
                    <Select value={cfg.sip?.transport || "UDP"} onValueChange={(v) => patch({ sip: { ...cfg.sip, transport: v } })}>
                      <SelectTrigger data-testid="transfer-sip-transport"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="UDP">UDP</SelectItem><SelectItem value="TCP">TCP</SelectItem><SelectItem value="TLS">TLS</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Destination / extension</Label>
                  <Input data-testid="transfer-sip-destination" value={cfg.sip?.destination || ""} onChange={(e) => patch({ sip: { ...cfg.sip, destination: e.target.value } })} placeholder="1001" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label className="text-xs">SIP auth username (optional)</Label>
                    <Input data-testid="transfer-sip-auth-user" value={cfg.sip?.auth_username || ""} onChange={(e) => patch({ sip: { ...cfg.sip, auth_username: e.target.value } })} placeholder="only if server asks for login" autoComplete="off" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">SIP auth password (optional)</Label>
                    <Input data-testid="transfer-sip-auth-pass" type="password" value={cfg.sip?.auth_password || ""} onChange={(e) => patch({ sip: { ...cfg.sip, auth_password: e.target.value } })} placeholder="only if server asks for login" autoComplete="new-password" /></div>
                </div>
                <p className="text-[11px] text-muted-foreground">Fill these only if your SIP/PBX server requires a login (returns 401/407). Leave blank for IP-trusted servers.</p>
                <div className="space-y-1.5"><Label className="text-xs">Additional SIP params (optional)</Label>
                  <Textarea
                    rows={4}
                    data-testid="transfer-sip-extra"
                    value={cfg.sip?.extra || ""}
                    onChange={(e) => patch({ sip: { ...cfg.sip, extra: e.target.value } })}
                    placeholder={"X-WA-Name={name}\nX-WA-Email={email}\nX-WA-Mobile={mobile}"}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    One SIP header per line as <code className="text-[10px]">Header-Name=value</code>.
                    Use Collect Customer Info keys in braces: <code className="text-[10px]">{"{name}"}</code>,{" "}
                    <code className="text-[10px]">{"{email}"}</code>,{" "}
                    <code className="text-[10px]">{"{mobile}"}</code>. These become custom headers on the transfer INVITE.
                  </p>
                </div>
              </div>
            ) : (cfg.transfer_type === "did") ? (
              <div className="space-y-1.5"><Label className="text-xs">DID / telephone number</Label>
                <Input data-testid="transfer-did-number" value={cfg.did?.number || ""} onChange={(e) => patch({ did: { ...cfg.did, number: e.target.value } })} placeholder="+14155550123" /></div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">Inbound group</Label>
                <Select
                  value={inboundGroupId(cfg) || undefined}
                  onValueChange={(v) => {
                    const g = (inboundGroups || []).find((x) => x.id === v);
                    patch({
                      inbound_group_id: v,
                      inbound_group: { id: v, name: g?.name || cfg.inbound_group?.name || "" },
                    });
                  }}
                >
                  <SelectTrigger data-testid="transfer-inbound-group-select"><SelectValue placeholder="Select inbound group" /></SelectTrigger>
                  <SelectContent>
                    {(inboundGroups || []).map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.name}{g.status && g.status !== "active" ? ` (${g.status})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!(inboundGroups || []).length && (
                  <p className="text-[11px] text-destructive" data-testid="transfer-inbound-group-empty">
                    No inbound groups on this account. Create one under Routing → Inbound Groups.
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  The live call is routed to this inbound group: Ready agents are connected, logged-in agents who are not Ready place the caller on hold, and no logged-in agent ends the call as Missed.
                </p>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">Two branches: <b>Success</b> and <b>Failure</b>. Destination belongs to this node only. SIP/DID/inbound-group execution happens when the call reaches this node.</p>
          </div>
        )}

        {kind === "hangup" && (
          <div className="space-y-1.5">
            <Label className="text-xs">Hangup reason / label (optional)</Label>
            <Input data-testid="hangup-reason" value={cfg.reason || ""} onChange={(e) => patch({ reason: e.target.value })} placeholder="not_interested" />
            <p className="text-[11px] text-muted-foreground">Represents the telephony hangup action. Connect it to an <b>End</b> node to mark flow completion.</p>
          </div>
        )}

        {kind === "end" && (
          <p className="text-xs text-muted-foreground">Terminal node — marks the campaign flow as complete. No configuration needed.</p>
        )}
      </div>
    </div>
  );
}

function validateFlow(nodes, edges, collectTts, aiBots) {
  const errors = [];
  const starts = nodes.filter((n) => n.data?.kind === "start");
  if (starts.length === 0) errors.push("The flow must contain a Start node.");
  else if (starts.length > 1) errors.push("The flow must contain exactly one Start node.");

  const incoming = {}, outgoing = {};
  edges.forEach((e) => {
    (outgoing[e.source] = outgoing[e.source] || []).push(e);
    (incoming[e.target] = incoming[e.target] || []).push(e);
  });
  if (starts[0] && !(outgoing[starts[0].id] || []).length) errors.push("The Start node must connect to at least one node.");

  const lbl = (n) => n.data?.label || (n.data?.kind || "node");
  const needsOutgoing = new Set(["play_audio", "collect_info", "ai_llm", "wait", "silence", "intent", "dtmf"]);
  const hasAiLlm = nodes.some((n) => n.data?.kind === "ai_llm");
  if (hasAiLlm && !(aiBots || []).length) {
    errors.push("An AI Agent node needs an AI Bot assigned to this account.");
  }
  nodes.forEach((n) => {
    const kind = n.data?.kind, cfg = n.data?.config || {};
    if (kind !== "start" && !(incoming[n.id] || []).length) errors.push(`“${lbl(n)}” is not connected to the flow.`);
    if (needsOutgoing.has(kind) && !(outgoing[n.id] || []).length) errors.push(`“${lbl(n)}” has no next step — connect it onward to another node or to a Hang Up/End node, otherwise the call drops as a dead end.`);
    if (kind === "intent" && !(cfg.responses || []).length) errors.push(`Customer Response node “${lbl(n)}” must have at least one response option.`);
    if (kind === "ai_llm") {
      if (!(cfg.objective || "").trim()) errors.push(`AI Agent node “${lbl(n)}” needs an objective.`);
      if (!(cfg.allowed_actions || []).length) errors.push(`AI Agent node “${lbl(n)}” needs at least one allowed action.`);
      else {
        const handles = new Set([...(cfg.allowed_actions || []), "max_duration", "inactivity"]);
        const outs = outgoing[n.id] || [];
        const missing = [...handles].filter((h) => !outs.some((e) => e.sourceHandle === h));
        if (missing.length) errors.push(`AI Agent node “${lbl(n)}” needs a connection for each allowed action (${missing.join(", ")}).`);
      }
      if ((collectTts?.engine === "piper" || collectTts?.paid) && collectTts?.voices?.length && !(cfg.voice || "").trim()) {
        errors.push(`AI Agent node “${lbl(n)}” needs a voice.`);
      }
      const maxM = Number(cfg.max_duration_minutes);
      if (!Number.isFinite(maxM) || maxM < 1) {
        errors.push(`AI Agent node “${lbl(n)}” needs a Maximum Talk Time of at least 1 minute.`);
      }
      const idleS = Number(cfg.inactivity_timeout_seconds);
      if (!Number.isFinite(idleS) || idleS < 5) {
        errors.push(`AI Agent node “${lbl(n)}” needs a Customer Inactivity Timeout of at least 5 seconds.`);
      }
    }
    if (kind === "dtmf" && !(cfg.keys || []).length) errors.push(`DTMF node “${lbl(n)}” must have at least one configured key.`);
    if (kind === "transfer") {
      const t = cfg.transfer_type;
      if (t === "sip") { if (!cfg.sip?.host?.trim() || !cfg.sip?.destination?.trim()) errors.push(`Transfer node “${lbl(n)}” needs a SIP server and destination.`); }
      else if (t === "did") { if (!cfg.did?.number?.trim()) errors.push(`Transfer node “${lbl(n)}” needs a DID/phone number.`); }
      else if (t === "inbound_group") {
        if (!inboundGroupId(cfg)) errors.push(`Transfer node “${lbl(n)}” needs an inbound group.`);
      }
      else errors.push(`Transfer node “${lbl(n)}” needs a transfer destination.`);
    }
    if (kind === "play_audio") {
      const src = (cfg.source || "upload").toLowerCase();
      if (src === "tts") {
        if (!cfg.audio_file_id || !cfg.tts_clip_id) {
          errors.push(`Play Audio node “${lbl(n)}” needs a Text-to-Speech voice selected.`);
        }
      } else if (!cfg.audio_file_id && !cfg.prompt_text?.trim()) {
        errors.push(`Play Audio node “${lbl(n)}” needs an audio file or text prompt.`);
      }
    }
    if (kind === "collect_info") {
      const on = (cfg.fields || []).filter((f) => f.enabled);
      if (!on.length) errors.push(`Collect Customer Info node “${lbl(n)}” needs at least one field.`);
      if ((collectTts?.engine === "piper" || collectTts?.paid) && collectTts?.voices?.length && !(cfg.voice || "").trim()) {
        errors.push(`Collect Customer Info node “${lbl(n)}” needs a voice.`);
      }
      const to = Number(cfg.timeout_seconds);
      if (!Number.isFinite(to) || to < 3) {
        errors.push(`Collect Customer Info node “${lbl(n)}” needs a no-reply timeout of at least 3 seconds.`);
      }
      const onTimeout = cfg.on_timeout != null ? cfg.on_timeout : "hangup";
      if (onTimeout === "goto" || onTimeout === "transfer") {
        const outs = outgoing[n.id] || [];
        if (!outs.some((e) => e.sourceHandle === "done" || e.sourceHandle == null)) {
          errors.push(`Collect Customer Info node “${lbl(n)}” needs a Done connection.`);
        }
        if (!outs.some((e) => e.sourceHandle === "timeout")) {
          errors.push(`Collect Customer Info node “${lbl(n)}” needs a Timeout connection.`);
        }
      }
      on.forEach((f) => {
        const isCustom = String(f.key || "").startsWith("custom");
        const label = (isCustom && f.custom_label) ? f.custom_label : f.label;
        if (isCustom && !(f.custom_label || "").trim()) {
          errors.push(`Collect Customer Info node “${lbl(n)}” needs a name for the custom field.`);
        }
        if (!(f.question || "").trim()) {
          errors.push(`Collect Customer Info node “${lbl(n)}” needs a question for ${label}.`);
        }
      });
    }
  });
  return errors;
}

function Inner({ initial, onSave, readOnly, saving }) {
  const START_NODE = { id: "start", type: "flowNode", position: { x: 400, y: 0 }, data: { kind: "start", label: "Start", config: {} } };
  const [nodes, setNodes, onNodesChange] = useNodesState(initial?.nodes?.length ? initial.nodes : [START_NODE]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    (initial?.edges || []).map((e) => ({ ...e, type: "deletable", data: { ...(e.data || {}), readOnly } }))
  );
  const [selectedId, setSelectedId] = useState(null);
  const [errors, setErrors] = useState([]);
  const [collectTts, setCollectTts] = useState(null);
  const [aiBots, setAiBots] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [inboundGroups, setInboundGroups] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const importInputRef = useRef(null);
  const { fitView } = useReactFlow();
  useEffect(() => {
    let live = true;
    api.get("/speech/collect-voices")
      .then(({ data }) => { if (live) setCollectTts(data); })
      .catch(() => { if (live) setCollectTts({ paid: false, voices: [] }); });
    api.get("/ai-bots/available-bots")
      .then(({ data }) => { if (live) setAiBots(Array.isArray(data) ? data : []); })
      .catch(() => { if (live) setAiBots([]); });
    api.get("/knowledge-bases")
      .then(({ data }) => { if (live) setKnowledgeBases(Array.isArray(data) ? data : []); })
      .catch(() => { if (live) setKnowledgeBases([]); });
    api.get("/customer-routing/inbound-groups")
      .then(({ data }) => { if (live) setInboundGroups(Array.isArray(data) ? data : []); })
      .catch(() => { if (live) setInboundGroups([]); });
    return () => { live = false; };
  }, []);

  // Keep AI Agent nodes on the customer's assigned bot (and a default voice when needed).
  useEffect(() => {
    const bot = (aiBots || [])[0];
    const defaultVoice = (collectTts?.engine === "piper" || collectTts?.paid) && collectTts?.voices?.[0]
      ? collectTts.voices[0].name : null;
    if (!bot && !defaultVoice) return;
    setNodes((nds) => {
      let changed = false;
      const next = nds.map((n) => {
        if (n.data?.kind !== "ai_llm") return n;
        const cfg = n.data.config || {};
        const patch = {};
        if (bot && cfg.ai_bot_id !== bot.id) {
          patch.ai_bot_id = bot.id;
          patch.ai_bot_name = bot.name || "";
        }
        if (defaultVoice && !(cfg.voice || "").trim()) patch.voice = defaultVoice;
        if (!Object.keys(patch).length) return n;
        changed = true;
        return { ...n, data: { ...n.data, config: { ...cfg, ...patch } } };
      });
      return changed ? next : nds;
    });
  }, [aiBots, collectTts, setNodes]);

  const nodeTypes = useMemo(() => ({ flowNode: FlowNode }), []);
  const edgeTypes = useMemo(() => ({ deletable: DeletableEdge }), []);

  const onConnect = useCallback((c) => {
    setEdges((eds) => {
      // One outgoing edge per source handle — reconnect replaces the old one.
      const cleaned = eds.filter(
        (e) => !(e.source === c.source && (e.sourceHandle || null) === (c.sourceHandle || null)),
      );
      const src = nodes.find((n) => n.id === c.source);
      return addEdge({
        ...c,
        type: "deletable",
        data: { readOnly: false },
        label: src ? handleLabel(src, c.sourceHandle) : undefined,
      }, cleaned);
    });
  }, [setEdges, nodes]);

  const addNode = (kind) => {
    const id = `${kind}_${Date.now().toString(36)}`;
    const bot = (aiBots || [])[0];
    const defaultVoice = (collectTts?.engine === "piper" || collectTts?.paid) && collectTts?.voices?.[0]
      ? collectTts.voices[0].name : null;
    setNodes((nds) => {
      const maxY = nds.reduce((m, n) => Math.max(m, n.position?.y || 0), 0);
      return nds.concat({
        id, type: "flowNode",
        position: { x: 380, y: maxY + 150 },
        data: { kind, label: KIND[kind].label, config: {
          ...defaultConfig(kind),
          ...(kind === "collect_info" && defaultVoice ? { voice: defaultVoice } : {}),
          ...(kind === "ai_llm" ? {
            ...(bot ? { ai_bot_id: bot.id, ai_bot_name: bot.name || "" } : {}),
            ...(defaultVoice ? { voice: defaultVoice } : {}),
          } : {}),
        } },
      });
    });
    setSelectedId(id);
  };

  const selectedNode = nodes.find((n) => n.id === selectedId) || null;

  const applyWizardFlow = (flow) => {
    const nextNodes = (flow?.nodes || []).map((n) => ({ ...n, type: "flowNode" }));
    const nextEdges = (flow?.edges || []).map((e) => ({
      ...e,
      type: "deletable",
      data: { ...(e.data || {}), readOnly: false },
    }));
    setNodes(nextNodes);
    setEdges(nextEdges);
    setSelectedId(null);
    setErrors([]);
    window.setTimeout(() => {
      try { fitView({ padding: 0.2, duration: 300 }); } catch { /* noop */ }
    }, 50);
  };

  const hasExistingFlow = nodes.some((n) => n.data?.kind && n.data.kind !== "start") || edges.length > 0;

  const cleanEdgeList = (list) => list.map(({ data, ...e }) => {
    const rest = { ...(data || {}) }; delete rest.readOnly;
    return Object.keys(rest).length ? { ...e, data: rest } : e;
  });

  const exportFlow = () => {
    try {
      const selected = selectedId ? nodes.find((n) => n.id === selectedId) : null;
      let payload;
      let filename;
      if (selected) {
        // Export only the selected node (no edges).
        const { selected: _s, dragging: _d, positionAbsolute: _pa, ...node } = selected;
        payload = {
          version: 1,
          exported_at: new Date().toISOString(),
          partial: true,
          nodes: [node],
          edges: [],
        };
        const kind = selected.data?.kind || "node";
        const label = (selected.data?.label || kind).replace(/[^\w.-]+/g, "_").slice(0, 40);
        filename = `call-flow-node-${kind}-${label}.json`;
      } else {
        payload = {
          version: 1,
          exported_at: new Date().toISOString(),
          partial: false,
          nodes,
          edges: cleanEdgeList(edges),
        };
        filename = `call-flow-${new Date().toISOString().slice(0, 10)}.json`;
      }
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(selected
        ? `Exported node “${selected.data?.label || selected.data?.kind || selected.id}”`
        : "Call flow exported");
    } catch (e) {
      toast.error("Failed to export call flow");
    }
  };

  const mergePartialNodes = (incomingNodes) => {
    const stamp = Date.now().toString(36);
    const idMap = {};
    const maxY = nodes.reduce((m, n) => Math.max(m, n.position?.y || 0), 0);
    let offset = 0;
    const nextNodes = incomingNodes.map((n) => {
      const kind = n?.data?.kind || "node";
      // Never duplicate the Start node — skip if kind is start
      if (kind === "start") return null;
      const newId = `${kind}_${stamp}_${offset++}`;
      idMap[n.id] = newId;
      return {
        ...n,
        id: newId,
        type: "flowNode",
        position: {
          x: (n.position?.x ?? 380),
          y: maxY + 150 + (offset - 1) * 40,
        },
        data: { ...(n.data || {}) },
      };
    }).filter(Boolean);
    if (!nextNodes.length) {
      toast.error("Nothing to import (Start node cannot be imported as a partial)");
      return;
    }
    setNodes((nds) => nds.concat(nextNodes));
    setSelectedId(nextNodes[0]?.id || null);
    setErrors([]);
    window.setTimeout(() => {
      try { fitView({ padding: 0.2, duration: 300 }); } catch { /* noop */ }
    }, 50);
    toast.success(`Imported ${nextNodes.length} node${nextNodes.length === 1 ? "" : "s"} into the flow`);
  };

  const importFlowFromFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      let raw;
      try {
        raw = JSON.parse(text);
      } catch {
        toast.error("Invalid JSON file");
        return;
      }
      // Accept either { nodes, edges } or wrapped { flow: { nodes, edges } }
      const flow = raw?.nodes ? raw : raw?.flow;
      if (!flow || !Array.isArray(flow.nodes)) {
        toast.error("File must contain a call flow with nodes");
        return;
      }
      const edgesIn = Array.isArray(flow.edges) ? flow.edges : [];
      const isPartial = raw?.partial === true
        || flow.partial === true
        || (flow.nodes.length === 1 && !flow.nodes.some((n) => n?.data?.kind === "start" || n?.id === "start"));

      if (isPartial) {
        mergePartialNodes(flow.nodes);
        return;
      }

      if (!flow.nodes.some((n) => n?.data?.kind === "start" || n?.id === "start")) {
        toast.error("Full flow import must include a Start node (or export a single selected node)");
        return;
      }
      if (hasExistingFlow) {
        const ok = window.confirm(
          "Import will replace the current call flow on this canvas. Continue?",
        );
        if (!ok) return;
      }
      applyWizardFlow({ nodes: flow.nodes, edges: edgesIn });
      toast.success(`Imported ${flow.nodes.length} nodes · ${edgesIn.length} connections`);
    } catch (e) {
      toast.error("Failed to import call flow");
    } finally {
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const save = (keepOpen = false) => {
    const errs = validateFlow(nodes, edges, collectTts, aiBots);
    setErrors(errs);
    // "Save flow" finalizes and requires a valid flow. "Save and Continue" saves a
    // work-in-progress draft even when incomplete, so you never lose edits mid-design.
    if (errs.length && !keepOpen) {
      toast.error(`Fix ${errs.length} issue${errs.length === 1 ? "" : "s"} below before saving the flow`);
      return;
    }
    const cleanEdges = edges.map(({ data, ...e }) => {
      const rest = { ...data }; delete rest.readOnly;
      return Object.keys(rest).length ? { ...e, data: rest } : e;
    });
    onSave({ nodes, edges: cleanEdges }, { keepOpen, draft: keepOpen });
  };

  return (
    <div className="flex flex-col h-[70vh]">
      <div className="flex items-center justify-between gap-2 pb-2 flex-wrap">
        <p className="text-xs text-muted-foreground">{nodes.length} nodes · {edges.length} connections</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            data-testid="export-flow-btn"
            onClick={exportFlow}
            title={selectedId
              ? "Export the selected node only"
              : "Export the full call flow (select a node to export only that node)"}
          >
            <DownloadSimple className="w-4 h-4 mr-1.5" />
            {selectedId ? "Export node" : "Export"}
          </Button>
          {!readOnly && (
            <>
              <input
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                data-testid="import-flow-input"
                onChange={(e) => importFlowFromFile(e.target.files?.[0])}
              />
              <Button
                type="button"
                variant="outline"
                className="rounded-lg"
                data-testid="import-flow-btn"
                onClick={() => importInputRef.current?.click()}
                title="Import a call flow JSON file onto this canvas"
              >
                <UploadSimple className="w-4 h-4 mr-1.5" /> Import
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="rounded-lg"
                data-testid="ai-agent-wizard-btn"
                onClick={() => setWizardOpen(true)}
                title="Guided setup — build an AI Agent call flow from simple answers"
              >
                <MagicWand className="w-4 h-4 mr-1.5" weight="fill" /> AI Agent Wizard
              </Button>
              <Button onClick={() => save(true)} disabled={saving} data-testid="save-continue-flow-btn" variant="outline" className="rounded-lg" title="Save your progress (even if incomplete) and keep editing">{saving ? "Saving…" : "Save and Continue"}</Button>
              <Button onClick={() => save(false)} disabled={saving} data-testid="save-flow-btn" className="rounded-lg" title="Validate and save the finished flow, then close">{saving ? "Saving…" : "Save flow"}</Button>
            </>
          )}
        </div>
      </div>

      <AiAgentWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onApply={applyWizardFlow}
        aiBots={aiBots}
        collectTts={collectTts}
        hasExistingFlow={hasExistingFlow}
      />

      {errors.length > 0 && (
        <div className="mb-2 rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 max-h-40 overflow-y-auto" data-testid="validation-errors">
          <p className="text-sm font-medium text-[hsl(var(--warning))] flex items-center gap-1.5"><WarningCircle className="w-4 h-4" weight="fill" /> {errors.length} item{errors.length === 1 ? "" : "s"} to finish before this flow can go live — <span className="font-normal">“Save and Continue” still saves your draft.</span></p>
          <ul className="mt-1 text-xs text-[hsl(var(--warning))] list-disc pl-5 space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="flex gap-3 flex-1 min-h-0">
        {!readOnly && (
          <div className="w-48 shrink-0 space-y-1.5 overflow-y-auto pr-1" data-testid="node-palette">
            <p className="text-xs uppercase tracking-wide text-muted-foreground px-1 pb-1">Add node</p>
            {PALETTE.map((k) => (
              <button key={k} onClick={() => addNode(k)} data-testid={`add-node-${k}`}
                className="w-full flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-left text-sm leading-tight hover:bg-muted transition-colors">
                <span className="w-6 h-6 rounded-md grid place-items-center text-white shrink-0" style={{ background: KIND[k].color }}>
                  {(() => { const I = KIND[k].icon; return <I className="w-3.5 h-3.5" weight="bold" />; })()}
                </span>
                {KIND[k].label}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 rounded-xl border border-border overflow-hidden bg-muted/20 min-w-0" data-testid="flow-canvas">
          <ReactFlow
            nodes={nodes} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes}
            defaultEdgeOptions={{ type: "deletable", data: { readOnly } }}
            onNodesChange={readOnly ? undefined : onNodesChange}
            onEdgesChange={readOnly ? undefined : onEdgesChange}
            onConnect={readOnly ? undefined : onConnect}
            isValidConnection={() => true}
            connectionRadius={28}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onPaneClick={() => setSelectedId(null)}
            nodesDraggable={!readOnly} nodesConnectable={!readOnly} elementsSelectable={!readOnly}
            edgesFocusable={!readOnly}
            // Keep Delete for nodes, but never Backspace — it steals keystrokes from
            // the side config panel number/text fields while a node is selected.
            deleteKeyCode={readOnly ? null : "Delete"}
            fitView proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </div>
        {!readOnly && selectedNode && (
          <ConfigPanel node={selectedNode} setNodes={setNodes} setEdges={setEdges} onClose={() => setSelectedId(null)} collectTts={collectTts} aiBots={aiBots} knowledgeBases={knowledgeBases} inboundGroups={inboundGroups} />
        )}
      </div>
    </div>
  );
}

export default function FlowDesigner(props) {
  return (
    <ReactFlowProvider>
      <Inner {...props} />
    </ReactFlowProvider>
  );
}

// Phrases editor for Customer Response branches. Keeps a LOCAL text buffer so
// typing a space between words is never swallowed (the old version trimmed on
// every keystroke, turning "yes i am interested" into "yesiaminterested").
// The comma-separated list is parsed into the phrases array only on blur.
function PhrasesInput({ value, onCommit, testid }) {
  const [text, setText] = useState((value || []).join(", "));
  useEffect(() => {
    const parsed = text.split(",").map((s) => s.trim()).filter(Boolean);
    if ((value || []).join("\u0000") !== parsed.join("\u0000")) {
      setText((value || []).join(", "));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const commit = () => onCommit(text.split(",").map((s) => s.trim()).filter(Boolean));
  return (
    <Input
      className="h-8 text-xs"
      data-testid={testid}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      placeholder="phrases: yes, i'm interested, sure"
    />
  );
}
